import axios from 'axios';
import { BACKEND_URL, signedHeaders } from './apiClient';

/** The fields ts-api's `ErrorReportDto` accepts. Everything but the first two is for the header block. */
export interface ErrorReportPayload {
  reference: string;
  report: string;
  note?: string;
  kind?: string;
  sentryEventId?: string;
  replayId?: string;
  environment?: string;
  release?: string;
}

/** A slow API must not hold the report dialog open indefinitely — the mail-client channel is one click away. */
const SEND_TIMEOUT_MS = 15000;

class SupportService {

  /**
   * Hand a crash report to ts-api, which emails it to the support recipients it holds in its own env.
   *
   * Deliberately **not** through `apiClient`. Two reasons, both about the report being the last thing
   * still working during a failure:
   *
   *  1. `installHttpErrorInterceptor` calls `onSessionExpired` on a 401, so a report that happens to
   *     be sent with a lapsed signature would log the user out — turning "your report failed" into
   *     "and now you are signed out", with the report lost.
   *  2. The interceptor pushes every response onto the action trail, so sending a report would
   *     append to the very trail it just sent, and a second attempt would carry the first.
   *
   * Resolves false rather than throwing: the caller's next move is the mail client, not a stack trace.
   */
  async sendErrorReport(payload: ErrorReportPayload): Promise<boolean> {
    const uri = 'support/error-report';
    try {
      const response = await axios.post(`${BACKEND_URL}/${uri}`, payload, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...signedHeaders('POST', uri)
        },
        timeout: SEND_TIMEOUT_MS
      });
      return response.status >= 200 && response.status < 300;
    } catch {
      return false;
    }
  }
}

export default new SupportService();
